import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { ClinicalDocument } from '@/features/encounters/api';
import { CLINIC_TZ } from '@/lib/clinic-time';
import { verifyUrl } from './consult-utils';
import {
  maskedId,
  slipDateLabel,
  timeSlipData,
  type TimeSlipContext,
} from './time-slip';
import styles from './TimeSlipPrintSheet.module.css';

/**
 * The printable time slip: its own print root, a direct child of <body>, hidden on screen. It says
 * attendance only; the slip is not a medical certificate. `onDone` must be stable (it re-runs the
 * print otherwise).
 */
export function TimeSlipPrintSheet({
  document: doc,
  context,
  onDone,
}: {
  document: ClinicalDocument;
  context: TimeSlipContext;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const data = timeSlipData(doc);
  const [qr, setQr] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let live = true;
    QRCode.toDataURL(verifyUrl(doc.verificationCode), { margin: 0, width: 320 })
      .then((src) => live && setQr(src))
      .catch(() => undefined)
      .finally(() => live && setReady(true));
    return () => {
      live = false;
    };
  }, [doc.verificationCode]);

  useEffect(() => {
    if (!ready) return;
    window.addEventListener('afterprint', onDone, { once: true });
    window.print();
    return () => window.removeEventListener('afterprint', onDone);
  }, [ready, onDone]);

  if (!data) return null;
  const patientId = maskedId(context.identity?.value);
  return createPortal(
    <div
      className={styles.printRoot}
      data-time-slip-print-root=""
      data-testid="time-slip-print-sheet"
    >
      <div className={styles.sheet}>
        <header className={styles.head}>
          <strong>Ommri Care</strong>
          <span>{context.branchName}</span>
        </header>
        <h1 className={styles.title}>{t('timeSlip.title')}</h1>
        <p className={styles.number}>{doc.number}</p>
        <dl className={styles.facts}>
          <dt>{t('timeSlip.patient')}</dt>
          <dd>
            {context.patientName}
            {patientId && (
              <span className={styles.mono}> · ID {patientId}</span>
            )}
          </dd>
          <dt>{t('timeSlip.for')}</dt>
          <dd>
            {data.forWhom === 'companion'
              ? t('timeSlip.forCompanion', {
                  name: data.companionName ?? '—',
                })
              : t('timeSlip.forPatient')}
            {data.companionId && (
              <span className={styles.mono}> · ID {data.companionId}</span>
            )}
          </dd>
          <dt>{t('timeSlip.date')}</dt>
          <dd>{slipDateLabel(data, doc.issuedAt)}</dd>
          <dt>{t('timeSlip.arrived')}</dt>
          <dd>{data.arrived}</dd>
          <dt>{t('timeSlip.leaving')}</dt>
          <dd>{data.leaving}</dd>
          <dt>{t('timeSlip.issuedBy')}</dt>
          <dd>
            {doc.issuedBy?.fullName ?? '—'}
            <span className={styles.sub}>
              {' '}
              ·{' '}
              {new Date(doc.issuedAt).toLocaleString('en-GB', {
                timeZone: CLINIC_TZ,
              })}
            </span>
          </dd>
        </dl>
        <p className={styles.notice}>{t('timeSlip.attendanceOnly')}</p>
        <div className={styles.qr}>
          {qr && <img src={qr} alt={t('timeSlip.qrAlt')} />}
          <div>
            <b>{t('timeSlip.scanToVerify')}</b>
            <span className={styles.mono}>
              {verifyUrl(doc.verificationCode)}
            </span>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
